import { useCallback, useMemo, useState } from 'react';

import { useApi } from '.';
import { ApiCommand } from '../Api';
import {
  type AppointmentCancelReasonOption,
  AppointmentStatus,
  OPEN_ZOOM_API_ERROR_TEXT,
  RecurringAppointmentUpdateOption,
  ZOOM_USER_DOES_NOT_EXIST_ERROR_TEXT,
} from '../constants/calendar.constants';
import { MaudPermissions } from '../constants/permissions.constants';
import urlConstants from '../constants/url.constants';
import { useGlobalContext } from '../contexts/GlobalContext';
import { hasSchedulingPermission } from '../util/patient.util';
import { useLazyGetProviderTeamAssignmentsQuery } from '~/store/services/clinicalData';
import {
  useLazyGetAllProvidersV2Query,
  useLazyGetProviderByIdQuery,
} from '~/store/services/providers';
import { isDateTodayOrInPast } from '../util/date.util';
import useErrorToast from './useErrorToast';
import { GlobalErrorCode } from '../constants/global.constants';

const {
  calendar: { getMeetingDetails: getZoomMeetingDetailsUrl },
} = urlConstants;

interface AppointmentsState {
  cancelAppointment: (
    appointmentExternalId: string,
    cancelType: RecurringAppointmentUpdateOption,
    cancellationReason: AppointmentCancelReasonOption,
    onSuccess: () => void,
    onFailure: (statusCode?: number, error?: string) => void,
  ) => void;
  cancelAppointmentError: string;
  getProviderTeamCareTeam: (patientId: string) => Promise<void>;
  getZoomUrl: (
    // TODO this is only used in one place - candidate for first removal
    appointmentExternalId: string,
    onSuccess: (url: string) => void,
    onFailure: (error: string, statusCode: number) => void,
  ) => void;
  hasSchedulingAuthorization: boolean;
  isLoading: boolean;
  patientCareTeams: CareTeamMember[];
  resetCancelAppointmentError: () => void;
}

/**
 * @deprecated trying to incrementally move this to the query library - use rtk query for new code
 */
const useAppointments = (): AppointmentsState => {
  const { showErrorToast } = useErrorToast();
  const { calendarAccount, currentUser, permissions } = useGlobalContext();
  const isSchedulingAdmin = permissions[MaudPermissions.MAUD_SCHEDULE_PROXY];
  const [getProviderTeamAssignments] = useLazyGetProviderTeamAssignmentsQuery();
  const [getProviderDetailsById] = useLazyGetProviderByIdQuery();
  const [getAllProviderDetails] = useLazyGetAllProvidersV2Query();

  const [careTeamProviders, setCareTeamProviders] = useState<
    CareTeamMember[] | null
  >(null);

  const {
    loading: zoomMeetingDetailsLoading,
    sendRequest: getZoomMeetingDetailsRequest,
  } = useApi<MeetingDetails>();

  const {
    loading: cancelAppointmentLoading,
    error: cancelAppointmentError,
    resetError: resetCancelAppointmentError,
    sendRequest: cancelAppointmentRequest,
  } = useApi<string>();

  const getProviderTeamWithDetails = useCallback(
    async (patientId: string) => {
      try {
        const providerTeamAssignments =
          await getProviderTeamAssignments(patientId).unwrap();

        let providers = [];
        let activeAssignments = [];

        if (providerTeamAssignments.treatmentEpisode?.providerTeamAssignments) {
          providers =
            providerTeamAssignments.treatmentEpisode.providerTeamAssignments;

          activeAssignments = providers.filter(
            (el) => !el.endDate || !isDateTodayOrInPast(el.endDate),
          );
        } else if (providerTeamAssignments.inquiry?.providerTeamAssignments) {
          providers = providerTeamAssignments.inquiry.providerTeamAssignments;
          activeAssignments = providers;
        } else {
          activeAssignments = [];
        }
        let assignmentsWithProviderInfo = [];
        if (activeAssignments.length > 0) {
          const providerIds = activeAssignments.map((a) => a.providerId);

          const batchRes = await getAllProviderDetails({
            id: providerIds,
            'include-avatar': true,
            'include-calendar-profile': true,
            'include-provider-history': true,
            'page-number': 1,
            'page-size': providerIds.length,
            'include-out-of-office': true,
          }).unwrap();

          assignmentsWithProviderInfo = activeAssignments.map((assignment) => {
            const provider = batchRes.data?.find(
              (p: GetProviderByIdResponse) =>
                p.userId === assignment.providerId,
            );

            return {
              avatarDetails: provider?.avatar ?? null,
              calendarProfile: provider?.calendarId ?? null,
              displayFirstName: provider?.displayFirstName ?? '',
              displayLastName: provider?.displayLastName ?? '',
              firstName: provider?.firstName ?? '',
              lastName: provider?.lastName ?? '',
              providerExternalId: assignment.providerId,
              sub: provider?.sub ?? null,
              timezone: provider?.timeZoneDetails?.code ?? null,
              outOfOfficeItems: provider?.outOfOffice ?? null,
              userId: assignment.providerId,
              providerTypeName: provider?.providerTypeDetails?.name ?? null,
            };
          });
        }
        return assignmentsWithProviderInfo;
      } catch (error) {
        console.error('Error fetching provider team with details:', error);
        showErrorToast(
          GlobalErrorCode.GET_CARE_TEAM,
          error.status ?? 500,
          error.data?.message,
        );
        return [];
      }
    },
    [getProviderTeamAssignments, getProviderDetailsById, showErrorToast],
  );

  const getProviderTeamCareTeam = useCallback(
    async (patientId: string) => {
      const providerTeam = await getProviderTeamWithDetails(patientId);
      setCareTeamProviders(providerTeam ?? []);
    },
    [getProviderTeamWithDetails],
  );

  const hasSchedulingAuthorization = useMemo<boolean>(
    () =>
      hasSchedulingPermission(
        currentUser.userId,
        calendarAccount?.profileConnected,
        isSchedulingAdmin,
        careTeamProviders,
      ),
    [
      calendarAccount?.profileConnected,
      careTeamProviders,
      currentUser.userId,
      isSchedulingAdmin,
    ],
  );

  const cancelAppointment = (
    appointmentExternalId: string,
    cancelType: RecurringAppointmentUpdateOption,
    cancellationReason: AppointmentCancelReasonOption,
    onSuccess: () => void,
    onFailure: (statusCode?: number, error?: string) => void,
  ): void => {
    cancelAppointmentRequest({
      callback: (_data: string, statusCode: number, error: string) => {
        if (statusCode === 200) {
          onSuccess();
        } else {
          onFailure(statusCode, error);
        }
      },
      command: ApiCommand.PATCH,
      options: {
        all: cancelType === RecurringAppointmentUpdateOption.ALL,
        appointmentExternalId,
        cancellationReason,
        status: AppointmentStatus.CANCELLED,
      },
      url: urlConstants.calendar.cancelAppointment,
    });
  };

  const getZoomUrl = (
    appointmentExternalId: string,
    onSuccess: (url: string) => void,
    onFailure: (error: string, statusCode: number) => void,
  ) => {
    getZoomMeetingDetailsRequest({
      callback: (
        meetingDetails: MeetingDetails,
        statusCode: number,
        error: string,
      ) => {
        if (statusCode === 200) {
          onSuccess(meetingDetails.startUrl ?? meetingDetails.joinUrl);
        } else {
          onFailure(
            error === ZOOM_USER_DOES_NOT_EXIST_ERROR_TEXT
              ? ZOOM_USER_DOES_NOT_EXIST_ERROR_TEXT
              : OPEN_ZOOM_API_ERROR_TEXT,
            statusCode,
          );
        }
      },
      command: ApiCommand.POST,
      options: {
        appointmentExternalId,
      },
      url: getZoomMeetingDetailsUrl,
    });
  };

  return {
    cancelAppointment,
    cancelAppointmentError,
    getProviderTeamCareTeam,
    getZoomUrl,
    hasSchedulingAuthorization,
    isLoading: zoomMeetingDetailsLoading || cancelAppointmentLoading,
    patientCareTeams: careTeamProviders,
    resetCancelAppointmentError,
  };
};

export default useAppointments;
